import { InputAdornment, TextField } from "@material-ui/core";
import Card from "@material-ui/core/Card";
import CardContent from "@material-ui/core/CardContent";
import { makeStyles } from "@material-ui/core/styles";
import Typography from "@material-ui/core/Typography";
import CardTitle from "@saleor/components/CardTitle";
import ControlledCheckbox from "@saleor/components/ControlledCheckbox";
import { FormSpacer } from "@saleor/components/FormSpacer";
import Hr from "@saleor/components/Hr";
import RadioGroupField from "@saleor/components/RadioGroupField";
import TextFieldWithChoice from "@saleor/components/TextFieldWithChoice";
import { DiscountErrorFragment } from "@saleor/fragments/types/DiscountErrorFragment";
import { FormChange } from "@saleor/hooks/useForm";
import { getFormErrors } from "@saleor/utils/errors";
import getDiscountErrorMessage from "@saleor/utils/errors/discounts";
import React, { useEffect } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import {
  DiscountValueTypeEnum,
  VoucherClassificationEnum,
  VoucherDiscountType,
  VoucherTypeEnum
} from "../../../types/globalTypes";
import { translateVoucherTypes } from "../../translations";
import { VoucherDetailsPageFormData } from "../VoucherDetailsPage";

interface VoucherValueProps {
  data: VoucherDetailsPageFormData;
  defaultCurrency: string;
  errors: DiscountErrorFragment[];
  clientErrors: {
    value?: string;
    maxDiscountValue?: string;
    tenantDiscountShare?: string;
    smopDiscountShare?: string;
  };
  disabled: boolean;
  variant: string;
  voucherMaxDiscountValue?: number;
  onChange: FormChange;
}

export enum VoucherType {
  ENTIRE_ORDER = "ENTIRE_ORDER",
  SPECIFIC_PRODUCT = "SPECIFIC_PRODUCT"
}

const useStyles = makeStyles(
  theme => ({
    checkBox: {
      display: "none"
    },
    hr: {
      margin: theme.spacing(2, 0)
    },
    textFieldGrid: {
      display: "grid",
      gridColumnGap: theme.spacing(2),
      gridTemplateColumns: "1fr 1fr"
    }
  }),
  {
    name: "VoucherValue"
  }
);

const VoucherValue: React.FC<VoucherValueProps> = props => {
  const {
    data,
    defaultCurrency,
    disabled,
    errors,
    clientErrors,
    variant,
    voucherMaxDiscountValue,
    onChange
  } = props;

  const classes = useStyles(props);
  const intl = useIntl();

  useEffect(() => {
    if (data.isSpecificCustomer) {
      onChange({ target: { name: "isHidden", value: false } });
    }
  }, [data.isSpecificCustomer]);

  const formErrors = getFormErrors(
    [
      "discountValue",
      "maxDiscountValue",
      "type",
      "smopDiscountShare",
      "tenantDiscountShare"
    ],
    errors
  );

  const translatedVoucherTypes = translateVoucherTypes(intl);
  const voucherTypeChoices = Object.values(VoucherType).map(type => ({
    disabled:
      type === "SPECIFIC_PRODUCT"
        ? data.verticalType === VoucherClassificationEnum.GROCERY
        : null,
    label: translatedVoucherTypes[type],
    value: type
  }));

  const eligibileCheck = ({ type: voucherType }) => {
    if (voucherType !== VoucherTypeEnum.SPECIFIC_PRODUCT) {
      onChange({ target: { name: "isMinEligibleOnly", value: false } });
    } else {
      onChange({ target: { name: "isMinEligibleOnly", value: true } });
    }
  };

  // This useEffect watches changes on voucher type
  // This will reset the value of applyOncePerOrder to false if the user unselects "Specific Products" by invoking resetOncePerOrderValue()
  useEffect(() => {
    eligibileCheck(data);
    if (data.type === "ENTIRE_ORDER" && data.smopDiscountShare === null) {
      onChange({
        target: { name: "smopDiscountShare", value: 100 }
      });
    }
  }, [data.type]);

  const handleNumberChange = e => {
    if (/^[.0-9\b]*$/.test(e.target.value)) {
      onChange(e);
    }
  };

  useEffect(() => {
    if (data.verticalType === "GROCERY") {
      onChange({
        target: { name: "type", value: VoucherTypeEnum.ENTIRE_ORDER }
      });
    }
  }, [data.verticalType]);

  const discountlabel =
    data.type.toString() === "E_VOUCHER" ||
    data.discountType.toString() === "E_VOUCHER"
      ? { label: "Selling Price", id: "sellingPriceTxt" }
      : data.discountType === DiscountValueTypeEnum.FIXED
      ? { label: "Discount Amount", id: "discountAmountTxt" }
      : { label: "Discount Percentage", id: "discountPercentageTxt" };

  const hideTenantSMOSubsidy =
    data.discountType.toString() === "E_VOUCHER" ||
    data.type === VoucherTypeEnum.SPECIFIC_PRODUCT;

  return (
    <Card>
      <CardTitle
        title={intl.formatMessage({
          defaultMessage: "Voucher Value",
          description: "section header"
        })}
      />
      <CardContent>
        {data.discountType.toString() ===
          VoucherDiscountType.PERCENTAGE_AND_CAPPED ||
        voucherMaxDiscountValue ? (
          <>
            <TextField
              id="discountCappedTxt"
              disabled={disabled}
              error={
                !!formErrors.maxDiscountValue || !!clientErrors.maxDiscountValue
              }
              label={intl.formatMessage({
                defaultMessage: "Discount Capped"
              })}
              value={data.maxDiscountValue}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Typography component="span" variant="caption">
                      PHP
                    </Typography>
                  </InputAdornment>
                )
              }}
              helperText={
                getDiscountErrorMessage(formErrors.maxDiscountValue, intl) ||
                clientErrors.maxDiscountValue
              }
              name={"maxDiscountValue" as keyof VoucherDetailsPageFormData}
              onChange={handleNumberChange}
              fullWidth
              inputProps={{
                min: 1
              }}
            />
            <FormSpacer />
            <TextField
              id="discountPercentageTxt"
              disabled={disabled}
              error={!!formErrors.discountValue || !!clientErrors.value}
              label={intl.formatMessage({
                defaultMessage: "Discount Percentage"
              })}
              value={data.value}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Typography component="span" variant="caption">
                      %
                    </Typography>
                  </InputAdornment>
                )
              }}
              helperText={
                getDiscountErrorMessage(formErrors.discountValue, intl) ||
                clientErrors.value
              }
              name={"value" as keyof VoucherDetailsPageFormData}
              onChange={handleNumberChange}
              fullWidth
            />
          </>
        ) : (
          <>
            <TextFieldWithChoice
              id={discountlabel.id}
              disabled={disabled}
              error={!!formErrors.discountValue || !!clientErrors?.value}
              ChoiceProps={{
                label:
                  data.discountType === DiscountValueTypeEnum.FIXED ||
                  data.discountType.toString() === "E_VOUCHER"
                    ? defaultCurrency
                    : "%",
                name: "discountType" as keyof VoucherDetailsPageFormData,
                values: null
              }}
              helperText={
                getDiscountErrorMessage(formErrors.discountValue, intl) ||
                clientErrors?.value
              }
              name={"value" as keyof VoucherDetailsPageFormData}
              onChange={handleNumberChange}
              label={discountlabel.label}
              value={data.value || ""}
              fullWidth
              inputProps={{
                min: 0
              }}
            />
          </>
        )}
        <FormSpacer />
        {!hideTenantSMOSubsidy && (
          <>
            <Hr className={classes.hr} />
            <div className={classes.textFieldGrid}>
              <TextField
                id="tenantTxt"
                disabled={disabled}
                error={
                  !!formErrors.tenantDiscountShare ||
                  !!clientErrors?.tenantDiscountShare
                }
                name="tenantDiscountShare"
                onChange={onChange}
                value={data.tenantDiscountShare}
                label={intl.formatMessage({
                  defaultMessage: "Tenant"
                })}
                helperText={
                  getDiscountErrorMessage(
                    formErrors.tenantDiscountShare,
                    intl
                  ) || clientErrors?.tenantDiscountShare
                }
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Typography component="span" variant="caption">
                        %
                      </Typography>
                    </InputAdornment>
                  )
                }}
                fullWidth
              />
              <TextField
                id="smopTxt"
                disabled={disabled}
                error={
                  !!formErrors.smopDiscountShare ||
                  !!clientErrors?.smopDiscountShare
                }
                name="smopDiscountShare"
                onChange={onChange}
                value={data.smopDiscountShare}
                label={intl.formatMessage({
                  defaultMessage: "SMOP"
                })}
                helperText={
                  getDiscountErrorMessage(formErrors.smopDiscountShare, intl) ||
                  clientErrors?.smopDiscountShare
                }
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Typography component="span" variant="caption">
                        %
                      </Typography>
                    </InputAdornment>
                  )
                }}
                fullWidth
              />
            </div>
          </>
        )}
        <FormSpacer />
        {variant === "create" && data.discountType.toString() !== "E_VOUCHER" && (
          <>
            <Hr className={classes.hr} />
            <RadioGroupField
              choices={voucherTypeChoices}
              disabled={disabled}
              error={!!formErrors.type}
              hint={getDiscountErrorMessage(formErrors.type, intl)}
              label={intl.formatMessage({
                defaultMessage: "Voucher Specific Information"
              })}
              name={"type" as keyof VoucherDetailsPageFormData}
              value={data.type}
              onChange={onChange}
            />
          </>
        )}

        {data.type === VoucherTypeEnum.SPECIFIC_PRODUCT && (
          <>
            <FormSpacer />
            <ControlledCheckbox
              id="eligibilityCheckbox"
              className={classes.checkBox}
              name={"isMinEligibleOnly" as keyof VoucherDetailsPageFormData}
              label={
                <>
                  <FormattedMessage
                    defaultMessage="Eligibility"
                    description="voucher application, switch button"
                  />
                  <Typography variant="caption">
                    <FormattedMessage defaultMessage="If this option is disabled, discount will be counted for every eligible product" />
                  </Typography>
                </>
              }
              checked={data.isMinEligibleOnly}
              disabled={disabled}
              onChange={onChange}
            />
          </>
        )}
        {!data.isSpecificCustomer && (
          <>
            <FormSpacer />
            <ControlledCheckbox
              id="hiddenCheckbox"
              name={"isHidden" as keyof VoucherDetailsPageFormData}
              label={
                <>
                  <FormattedMessage
                    defaultMessage="Hidden"
                    description="voucher hide, check box"
                  />
                </>
              }
              checked={data.isHidden}
              onChange={onChange}
              disabled={disabled}
            />
          </>
        )}
      </CardContent>
    </Card>
  );
};
export default VoucherValue;
